/**
 * Sync Queue Panel
 * A Drawer listing all offline-queued operations (pending / processing / failed).
 * Accessible by clicking the pending-count badge in NetworkStatusIndicator.
 *
 * Features:
 *  - "Sync now" button to force-replay the queue
 *  - Per-item delete button (any status)
 *  - Per-item retry button (failed items only — resets status to pending)
 */

import {
  Close as CloseIcon,
  Refresh as RetryIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSyncQueue } from '../../hooks/useSyncQueue';
import { resetFailedToPending, updateStatus } from '../../services/syncQueue';
import { useNetwork } from '../../store/NetworkContext';
import { DATE_TIME_SECONDS_FORMAT, formatDateTime } from '../../utils/dateFormat';

import type { SyncQueueItem } from '../../services/offlineDb';

// ─── Operation type label map ───────────────────────────────────────────────

const OP_TYPE_KEY: Record<string, string> = {
  checkpoint_arrival: 'syncQueue.opArrival',
  checkpoint_departure: 'syncQueue.opDeparture',
  checkpoint_event_create: 'syncQueue.opEventCreate',
  checkpoint_event_update: 'syncQueue.opEventUpdate',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function statusColor(status: SyncQueueItem['status']): 'warning' | 'error' | 'info' {
  if (status === 'failed') {return 'error';}
  if (status === 'processing') {return 'info';}
  return 'warning';
}

function extractRef(item: SyncQueueItem): string | null {
  const body = item.body as Record<string, unknown> | null | undefined;
  if (!body) {return null;}
  return (
    (body['lvo_reference'] as string | null) ??
    (body['freight_lot_id'] != null ? `#${String(body['freight_lot_id'])}` : null)
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SyncQueuePanel({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { isOnline, isProcessingQueue, triggerQueueReplay } = useNetwork();
  const { items, refresh } = useSyncQueue();

  const handleRetry = async (item: SyncQueueItem) => {
    if (item.id == null) {return;}
    await updateStatus(item.id, 'pending', 0);
    refresh();
  };

  const handleSyncNow = () => {
    void triggerQueueReplay();
  };

  const handleRetryAll = async () => {
    await resetFailedToPending();
    await refresh();
    void triggerQueueReplay();
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;

  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      onClose={onClose}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t('syncQueue.title')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Summary & sync button ── */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('syncQueue.summary', { pending: pendingCount, failed: failedCount })}
          </Typography>
        </Box>
        {failedCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RetryIcon fontSize="small" />}
            disabled={!isOnline || isProcessingQueue}
            onClick={() => void handleRetryAll()}
          >
            {t('syncQueue.retryAll')}
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={
            isProcessingQueue ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SyncIcon fontSize="small" />
            )
          }
          disabled={!isOnline || isProcessingQueue || pendingCount === 0}
          onClick={handleSyncNow}
        >
          {t('syncQueue.syncNow')}
        </Button>
      </Box>

      <Divider />

      {/* ── Items list ── */}
      {items.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {t('syncQueue.empty')}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {items
            .slice()
            .sort((a, b) => a.timestamp - b.timestamp)
            .map((item, idx) => {
              const opKey = OP_TYPE_KEY[item.operationType] ?? 'syncQueue.opUnknown';
              const ref = extractRef(item);
              const imageCount =
                (item.metadata as { images?: unknown[] } | null)?.images?.length ?? 0;

              return (
                <Box key={item.id ?? idx}>
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {item.status === 'failed' && (
                          <Tooltip title={t('syncQueue.retry')}>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => void handleRetry(item)}
                            >
                              <RetryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 8 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {t(opKey)}
                          </Typography>
                          <Chip
                            label={t(`syncQueue.status.${item.status}`)}
                            color={statusColor(item.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" component="span">
                            {formatDateTime(item.timestamp, DATE_TIME_SECONDS_FORMAT)}
                          </Typography>
                          {ref && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {t('syncQueue.ref')}: {ref}
                            </Typography>
                          )}
                          {imageCount > 0 && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {t('syncQueue.images', { count: imageCount })}
                            </Typography>
                          )}
                          {item.retryCount > 0 && (
                            <Typography variant="caption" color="error" component="span">
                              {t('syncQueue.retries', { count: item.retryCount, max: item.maxRetries })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < items.length - 1 && <Divider component="li" />}
                </Box>
              );
            })}
        </List>
      )}
    </Drawer>
  );
}
